// GameEventAudioSynthesizer.ts - Web Audio API procedural synthesis for table events and notifications
import { AudioSynthHelper } from './AudioSynthHelper';

export class GameEventAudioSynthesizer {
  /**
   * Trick won collection swoosh
   */
  public static playTrickWon(ctx: AudioContext) {
    AudioSynthHelper.playTone(ctx, {
      freq: 280,
      endFreq: 540,
      dur: 0.145,
      type: 'sine',
      gain: 0.38,
      attack: 0.005,
    });
  }

  /**
   * Bid placed chime
   */
  public static playBidChime(ctx: AudioContext) {
    const t = ctx.currentTime;
    [523.25, 659.25, 783.99].forEach((freq, idx) => {
      AudioSynthHelper.playTone(ctx, {
        freq,
        start: t + idx * 0.07,
        dur: 0.17,
        type: 'sine',
        gain: 0.38,
        attack: 0.004,
      });
    });
  }

  /**
   * Double-tap tone when passing during auction
   */
  public static playPassTone(ctx: AudioContext) {
    const t = ctx.currentTime;
    [0.0, 0.08].forEach((offset, idx) => {
      AudioSynthHelper.playTone(ctx, {
        freq: idx === 0 ? 350 : 290,
        endFreq: 130,
        start: t + offset,
        dur: 0.05,
        type: 'triangle',
        gain: 0.32,
        attack: 0.003,
      });
    });
  }

  /**
   * Bubble pop when a chat or reaction bubble appears
   */
  public static playChatBubblePop(ctx: AudioContext) {
    AudioSynthHelper.playTone(ctx, {
      freq: 450,
      endFreq: 1150,
      dur: 0.06,
      type: 'sine',
      gain: 0.30,
      attack: 0.004,
    });
  }

  /**
   * Observer chime when someone joins the table to watch (E5 -> B5)
   */
  public static playSpectatorJoinSound(ctx: AudioContext) {
    AudioSynthHelper.playNoteSequence(ctx, [
      { freq: 659.25, start: 0.00, dur: 0.14, gain: 0.30, type: 'sine' }, // E5
      { freq: 987.77, start: 0.08, dur: 0.28, gain: 0.36, type: 'sine' }, // B5
    ]);
  }

  /**
   * Light descending chime when a spectator stops watching (B5 -> E5)
   */
  public static playSpectatorLeaveSound(ctx: AudioContext) {
    AudioSynthHelper.playNoteSequence(ctx, [
      { freq: 987.77, start: 0.00, dur: 0.12, gain: 0.28, type: 'sine' }, // B5
      { freq: 659.25, start: 0.08, dur: 0.24, gain: 0.25, type: 'sine' }, // E5
    ]);
  }

  /**
   * Ascending arpeggio when someone joins table or sits down (F4 -> A4 -> C5)
   */
  public static playPlayerJoinSound(ctx: AudioContext) {
    AudioSynthHelper.playNoteSequence(ctx, [
      { freq: 349.23, start: 0.00, dur: 0.14, gain: 0.32, type: 'triangle', filter: { type: 'lowpass', freq: 1800, endFreq: 600 } }, // F4
      { freq: 440.00, start: 0.08, dur: 0.16, gain: 0.36, type: 'triangle', filter: { type: 'lowpass', freq: 1800, endFreq: 600 } }, // A4
      { freq: 523.25, start: 0.16, dur: 0.32, gain: 0.40, type: 'triangle', filter: { type: 'lowpass', freq: 1800, endFreq: 600 } }, // C5
    ]);
  }

  /**
   * Descending cue when someone leaves seat or table (C5 -> A4 -> F4)
   */
  public static playPlayerLeaveSound(ctx: AudioContext) {
    AudioSynthHelper.playNoteSequence(ctx, [
      { freq: 523.25, start: 0.00, dur: 0.14, gain: 0.32, type: 'triangle', filter: { type: 'lowpass', freq: 1600, endFreq: 500 } }, // C5
      { freq: 440.00, start: 0.08, dur: 0.14, gain: 0.28, type: 'triangle', filter: { type: 'lowpass', freq: 1600, endFreq: 500 } }, // A4
      { freq: 349.23, start: 0.16, dur: 0.30, gain: 0.26, type: 'triangle', filter: { type: 'lowpass', freq: 1600, endFreq: 500 } }, // F4
    ]);
  }

  /**
   * Subtle tick-tock reminder for active turn
   */
  public static playTurnTickTock(ctx: AudioContext) {
    const t = ctx.currentTime;
    // Woodblock tick
    AudioSynthHelper.playTone(ctx, {
      freq: 1200,
      endFreq: 300,
      start: t,
      dur: 0.045,
      type: 'sine',
      gain: 0.36,
    });
    // Secondary tock after 140ms
    AudioSynthHelper.playTone(ctx, {
      freq: 900,
      endFreq: 220,
      start: t + 0.14,
      dur: 0.045,
      type: 'sine',
      gain: 0.32,
    });
  }

  /**
   * Clock/woodblock tick when countdown timer is low
   */
  public static playTimerTick(ctx: AudioContext, isUrgent: boolean = false) {
    const freq = isUrgent ? 1174.66 : 880;
    AudioSynthHelper.playTone(ctx, {
      freq,
      endFreq: freq * 0.7,
      dur: isUrgent ? 0.08 : 0.045,
      type: isUrgent ? 'sine' : 'triangle',
      gain: isUrgent ? 0.32 : 0.22,
      attack: 0.003,
    });
  }

  /**
   * Golden auction chime when bidding turn arrives (D5 -> G5 -> B5 -> E6)
   */
  public static playYourBidTurn(ctx: AudioContext) {
    AudioSynthHelper.playNoteSequence(ctx, [
      { freq: 587.33, start: 0.00, dur: 0.22, gain: 0.36, type: 'sine' }, // D5
      { freq: 783.99, start: 0.08, dur: 0.26, gain: 0.38, type: 'sine' }, // G5
      { freq: 987.77, start: 0.16, dur: 0.32, gain: 0.42, type: 'sine' }, // B5
      { freq: 1318.51, start: 0.24, dur: 0.45, gain: 0.45, type: 'sine' }, // E6
    ]);
  }

  /**
   * Bell chime when player's card play turn arrives (G5 -> C6)
   */
  public static playYourTurn(ctx: AudioContext) {
    AudioSynthHelper.playNoteSequence(ctx, [
      { freq: 783.99, start: 0.00, dur: 0.16, gain: 0.36, type: 'sine' }, // G5
      { freq: 1046.50, start: 0.08, dur: 0.28, gain: 0.42, type: 'sine' }, // C6
    ]);
  }



  /**
   * Crisp, musical chime on each step of a score increment (pure tones, no static noise)
   */
  public static playScoreIncrementTick(ctx: AudioContext, step: number = 0) {
    const t = ctx.currentTime;
    // Musical subtle pitch ascent per step (A5 region: warm and clear)
    const pitchOffset = Math.min(step * 35, 210);
    const baseFreq = 880 + pitchOffset; // A5 region

    // Layer 1: Clean bell tone
    AudioSynthHelper.playTone(ctx, {
      freq: baseFreq,
      endFreq: baseFreq * 1.02,
      start: t,
      dur: 0.09,
      gain: 0.36,
      type: 'sine',
      attack: 0.002,
    });

    // Layer 2: Harmonic warmth
    AudioSynthHelper.playTone(ctx, {
      freq: baseFreq * 2.0,
      start: t,
      dur: 0.06,
      gain: 0.18,
      type: 'triangle',
      attack: 0.002,
      filter: { type: 'lowpass', freq: 2400 },
    });

    // Layer 3: Pure-tone micro transient click (zero static/white noise)
    AudioSynthHelper.playTone(ctx, {
      freq: baseFreq * 1.8,
      endFreq: baseFreq * 0.5,
      start: t,
      dur: 0.012,
      gain: 0.14,
      type: 'triangle',
      attack: 0.001,
    });
  }

  /**
   * Celebratory crystal chime ("Ding!") on landing final score increment (pure sine tones, zero static)
   */
  public static playScoreIncrementWhoop(ctx: AudioContext) {
    const t = ctx.currentTime;

    // Layer 1: Sweet upward bell chime step G5 (784Hz)
    AudioSynthHelper.playTone(ctx, {
      freq: 783.99, // G5
      start: t,
      dur: 0.15,
      gain: 0.45,
      type: 'sine',
      attack: 0.003,
    });

    // Layer 2: Crystal bell "Ding!" fundamental C6 (1046.5Hz)
    const dingTime = t + 0.08;
    AudioSynthHelper.playTone(ctx, {
      freq: 1046.50, // C6
      start: dingTime,
      dur: 0.60,
      gain: 0.60,
      type: 'sine',
      attack: 0.002,
    });

    // Layer 3: Silver overtone G6 (1568Hz)
    AudioSynthHelper.playTone(ctx, {
      freq: 1567.98, // G6
      start: dingTime,
      dur: 0.40,
      gain: 0.22,
      type: 'sine',
      attack: 0.002,
    });
  }

  /**
   * Warm, hollow "pong" resolution when score finishes counting down (e.g. failing bid)
   */
  public static playScoreDecrementLanding(ctx: AudioContext) {
    const t = ctx.currentTime;

    // Layer 1: Hollow low "pong" fundamental (G3 -> C3)
    AudioSynthHelper.playTone(ctx, {
      freq: 196.00,
      endFreq: 130.81,
      start: t,
      dur: 0.38,
      gain: 0.50,
      type: 'sine',
      attack: 0.004,
    });

    // Layer 2: Resonant hollow wooden body
    AudioSynthHelper.playTone(ctx, {
      freq: 261.63,
      endFreq: 130.81,
      start: t,
      dur: 0.26,
      gain: 0.35,
      type: 'triangle',
      attack: 0.003,
      filter: { type: 'lowpass', freq: 550 },
    });
  }

  /**
   * Snappy, dull downward thud ("bump") on each step of a score decrement
   */
  public static playScoreDecrementTick(ctx: AudioContext, step: number = 0) {
    const t = ctx.currentTime;
    // Dynamic subtle pitch drop per step
    const pitchOffset = Math.max(step * 6, 0);
    const startFreq = Math.max(160 - pitchOffset, 95);

    // Layer 1: Downward pitch thud
    AudioSynthHelper.playTone(ctx, {
      freq: startFreq,
      endFreq: startFreq * 0.55,
      start: t,
      dur: 0.08,
      gain: 0.48,
      type: 'sine',
      attack: 0.003,
      filter: { type: 'lowpass', freq: 280 },
    });

    // Layer 2: Mechanical knock transient
    AudioSynthHelper.playTone(ctx, {
      freq: startFreq * 1.8,
      endFreq: 50,
      start: t,
      dur: 0.04,
      gain: 0.32,
      type: 'triangle',
      attack: 0.002,
    });
  }

  /**
   * Informative chime notification when remaining tricks are claimed
   */
  public static playClaimNotification(ctx: AudioContext) {
    const t = ctx.currentTime;
    const notes = [
      { freq: 369.99, start: 0.00, dur: 0.22, gain: 0.28 }, // F#4
      { freq: 440.00, start: 0.10, dur: 0.24, gain: 0.30 }, // A4
      { freq: 554.37, start: 0.20, dur: 0.48, gain: 0.32 }, // C#5
    ];

    notes.forEach(({ freq, start, dur, gain: noteGain }) => {
      // Fundamental + subtle octave overtone
      AudioSynthHelper.playTone(ctx, {
        freq,
        start: t + start,
        dur,
        gain: noteGain,
        type: 'triangle',
        attack: 0.015,
        filter: { type: 'lowpass', freq: freq * 4, endFreq: freq * 1.5 },
      });
      AudioSynthHelper.playTone(ctx, {
        freq: freq * 2,
        start: t + start,
        dur,
        gain: noteGain * 0.4,
        type: 'sine',
        attack: 0.015,
        filter: { type: 'lowpass', freq: freq * 4, endFreq: freq * 1.5 },
      });
    });
  }

  /**
   * Authentic glass breaking / shattering sound effect when Hearts or Queen of Spades breaks hearts.
   * Multi-layered physical acoustic synthesis:
   * 1. Sharp glass fracture snap transient (fast noise impulse + high inharmonic resonant strikes).
   * 2. Violent explosive glass shattering burst (bandpassed resonant bursts at 2800Hz, 4100Hz, 5800Hz, 7600Hz).
   * 3. Heavy felt/table shockwave thump (punchy sub-felt impact).
   * 4. Cascading crystal shard fallout / scatter tinkles (delicate resonant pings across 0.07s - 0.40s).
   */
  public static playHeartsBroken(ctx: AudioContext) {
    const t = ctx.currentTime;

    // Layer 1: Sharp glass fracture snap transient (0 - 0.045s)
    AudioSynthHelper.playNoiseBurst(ctx, {
      start: t,
      dur: 0.045,
      gain: 0.55,
      attack: 0.001,
      decay: 0.015,
      filter: { type: 'highpass', freq: 3500, Q: 3.0 },
    });

    // High inharmonic fracture strikes (crystalline pane snap)
    [3800, 5200, 7100].forEach((freq, idx) => {
      AudioSynthHelper.playTone(ctx, {
        freq,
        endFreq: freq * 0.45,
        start: t + idx * 0.003,
        dur: 0.035,
        gain: 0.28,
        type: 'triangle',
        attack: 0.001,
      });
    });

    // Layer 2: Violent glass shattering explosion (0.015 - 0.22s)
    const shatterClusters = [
      { freq: 2800, q: 6.0, start: 0.015, dur: 0.16, gain: 0.42 },
      { freq: 4100, q: 8.0, start: 0.025, dur: 0.20, gain: 0.48 },
      { freq: 5800, q: 7.0, start: 0.035, dur: 0.18, gain: 0.38 },
      { freq: 7600, q: 9.0, start: 0.045, dur: 0.14, gain: 0.30 },
    ];

    shatterClusters.forEach(({ freq, q, start, dur, gain }) => {
      AudioSynthHelper.playNoiseBurst(ctx, {
        start: t + start,
        dur,
        gain,
        attack: 0.002,
        decay: 0.04,
        filter: { type: 'bandpass', freq, Q: q },
      });
    });

    // Layer 3: Heavy table/felt impact thump for tactile weight
    AudioSynthHelper.playTone(ctx, {
      freq: 190,
      endFreq: 45,
      start: t,
      dur: 0.09,
      gain: 0.45,
      type: 'triangle',
      attack: 0.002,
    });

    // Layer 4: Cascading crystal shard fallout / scatter tinkles (0.07 - 0.40s)
    const shards = [
      { freq: 4600, start: 0.07, dur: 0.04, gain: 0.22 },
      { freq: 6200, start: 0.11, dur: 0.035, gain: 0.20 },
      { freq: 3400, start: 0.14, dur: 0.05, gain: 0.25 },
      { freq: 5300, start: 0.19, dur: 0.04, gain: 0.18 },
      { freq: 7200, start: 0.23, dur: 0.03, gain: 0.16 },
      { freq: 4100, start: 0.28, dur: 0.045, gain: 0.19 },
      { freq: 5900, start: 0.33, dur: 0.035, gain: 0.14 },
    ];

    shards.forEach(({ freq, start, dur, gain: shardGain }) => {
      AudioSynthHelper.playTone(ctx, {
        freq,
        start: t + start,
        dur,
        gain: shardGain,
        type: 'sine',
        attack: 0.002,
        filter: { type: 'highpass', freq: 2800 },
      });
    });
  }
}
